import { useCallback, useMemo } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import { personDisplayName, usePersonNames } from '../../../human-resources/personnel/usePersonNames';

interface Named {
  personId?: string | null;
  employeeNumber?: string | null;
}

/**
 * Names for a set of employees carried as `personId`s — resolved in ONE batch, never stored.
 * Without `personnel.read` a name reads Hidden and the batch is not sent; a `personId` withheld by
 * the access level reads Restricted.
 */
export function useMemberNames(members: readonly Named[]): (member: Named) => string {
  const canReadPeople = usePermission('personnel.read');

  // `!= null`: a withheld id arrives as undefined, not null.
  const ids = useMemo(
    () => (canReadPeople ? members.map((m) => m.personId).filter((id): id is string => id != null) : []),
    [canReadPeople, members],
  );

  const names = usePersonNames(ids);

  return useCallback(
    (member: Named) =>
      !canReadPeople && member.personId != null
        ? 'Hidden'
        : personDisplayName({ personId: member.personId, employeeNumber: member.employeeNumber ?? '' }, names),
    [canReadPeople, names],
  );
}
